import { memo, useState } from 'react';

import Debug from '@/components/Debug';
import { Color } from '@/constants/color';
import { Prescription } from '@/types/medication';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import { isPharmacyVerified, prescriptionStatusLabel } from '@/utils/prescriptionStatus';

import PharmacyCard from '../PharmacyCard';

const PrescriptionCard = ({ prescription }: { prescription: Prescription }) => {
  const [showAdditionalInformation, setShowAdditionalInformation] = useState(false);

  const lastFillDate = prescription.last_fill_at ? new Date(prescription.last_fill_at) : null;
  const prescribedBy = prescription.prescriber_name || 'Affect Frontdesk';

  return (
    <Card className="p-4 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <Heading
          className="mp-sensitive text-lg font-semibold text-gray-900"
          text={prescription.product_name}
        />
      </div>
      <dl className="space-y-1 text-sm text-gray-600">
        <div>
          <dt className="font-medium inline">Last filled date:</dt>
          <dd className="inline ml-1">
            {lastFillDate ? <DualTime dateTime={lastFillDate} /> : 'None'}
          </dd>
        </div>
      </dl>
      <div className="mb-4">
        <Badge
          color={isPharmacyVerified(prescription.status) ? Color.GREEN : Color.YELLOW}
          text={prescriptionStatusLabel(prescription.status)}
        />
      </div>
      <dl className="space-y-1 text-sm text-gray-600">
        <div>
          <dt className="font-medium inline">Directions:</dt>
          <dd className="mp-sensitive inline ml-1">{prescription.directions}</dd>
        </div>
      </dl>

      <Button
        className="mt-2"
        color={Color.WHITE}
        onClick={() => setShowAdditionalInformation(!showAdditionalInformation)}
        size="xs">
        {showAdditionalInformation ? 'Hide Additional Information' : 'Additional Information'}
      </Button>
      {showAdditionalInformation && (
        <>
          <dl className="my-4 border-t border-b border-gray-200 py-4 mt-4" />
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Prescription Details</h3>

          <dl className="space-y-1 text-sm text-gray-600">
            {lastFillDate ? (
              <div>
                <dt className="font-medium inline">Last filled date:</dt>
                <dd className="inline ml-1 font-bold">
                  {lastFillDate ? <DualTime dateTime={lastFillDate} /> : 'None'}
                </dd>
              </div>
            ) : null}
            <div>
              <dt className="font-medium inline">Prescribed:</dt>
              <dd className="inline ml-1 font-bold">
                {prescription.written_at ? (
                  <DualTime dateTime={new Date(prescription.written_at ?? '')} />
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="font-medium inline">Prescribed by:</dt>
              <dd className="inline ml-1 font-bold">{prescribedBy}</dd>
            </div>
          </dl>

          <h3 className="text-sm font-semibold text-gray-900 mb-2 mt-4">Refill Details</h3>
          <dl className="space-y-1 text-sm text-gray-600">
            <div>
              <dt className="font-medium inline">Quantity:</dt>
              <dd className="inline ml-1 font-bold">{prescription.quantity}</dd>
            </div>
            <div>
              <dt className="font-medium inline">Day supply:</dt>
              <dd className="inline ml-1 font-bold">{prescription.days_supply}</dd>
            </div>
          </dl>

          <h3 className="text-sm font-semibold text-gray-900 mb-2 mt-4">Pharmacy Details</h3>
          <PharmacyCard pharmacy={prescription.pharmacy} />
        </>
      )}

      <Debug object={prescription as object} />
    </Card>
  );
};

export default memo(PrescriptionCard);
